import React from "react";
import { Link, useParams, useNavigate, NavLink, Routes, Route } from "react-router-dom";
import { api, API, resolveImg } from "../lib/api";
import { toast } from "sonner";

const REPORT_TYPES = [
  { v: "blog", label: "Blog article" },
  { v: "pdf", label: "PDF report" },
  { v: "both", label: "PDF + blog body" },
];

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1">{label}</label>
      {children}
      {hint && <p className="font-mono-tech text-[10px] text-zinc-400 mt-1">{hint}</p>}
    </div>
  );
}

const Input = (props) => (
  <input
    {...props}
    className={`w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833] ${props.className || ""}`}
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    rows={props.rows || 4}
    className={`w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833] ${props.className || ""}`}
  />
);

async function uploadFile(file, endpoint) {
  const fd = new FormData();
  fd.append("file", file);
  const token = localStorage.getItem("bw_admin_token");
  const res = await fetch(`${API}${endpoint}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(err || "Upload failed");
  }
  return res.json();
}

function ReportForm({ initial, onSubmit, submitLabel = "Save report" }) {
  const [form, setForm] = React.useState(() => ({
    title: "", slug: "", summary: "", preview_content: "", body_content: "",
    cover_image_url: "", pdf_url: "", report_type: "blog", category: "Quarterly",
    tags: [], is_featured: false, is_active: true, is_gated: true,
    ...(initial || {}),
  }));
  const [tagInput, setTagInput] = React.useState("");
  const [uploadingCover, setUploadingCover] = React.useState(false);
  const [uploadingPdf, setUploadingPdf] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingCover(true);
    try {
      const r = await uploadFile(f, "/upload/image");
      set("cover_image_url", r.url);
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(err.message);
    } finally { setUploadingCover(false); }
  };

  const handlePdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPdf(true);
    try {
      const r = await uploadFile(f, "/upload/pdf");
      set("pdf_url", r.url);
      toast.success("PDF uploaded");
    } catch (err) {
      toast.error(err.message);
    } finally { setUploadingPdf(false); }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  };

  const removeTag = (t) => set("tags", form.tags.filter((x) => x !== t));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.summary.trim()) {
      toast.error("Title and summary are required");
      return;
    }
    setSaving(true);
    try {
      await onSubmit(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-5">
        <Field label="Title *">
          <Input data-testid="market-form-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Q1 2026 Dubai Warehouse Market Report" />
        </Field>
        <Field label="Slug (URL)" hint="Leave blank to auto-generate from title">
          <Input data-testid="market-form-slug" value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="q1-2026-dubai-warehouse-market" />
        </Field>
        <Field label="Summary *" hint="One-line blurb shown on cards & SEO meta">
          <Textarea data-testid="market-form-summary" value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={2} />
        </Field>
        <Field label="Free preview content (markdown)" hint="Shown to everyone. Use ## headings, **bold**, - lists, [link](url)">
          <Textarea data-testid="market-form-preview" value={form.preview_content} onChange={(e) => set("preview_content", e.target.value)} rows={6} />
        </Field>
        <Field label="Full body content (markdown)" hint="Gated behind email if 'Email-gated' is on">
          <Textarea data-testid="market-form-body" value={form.body_content} onChange={(e) => set("body_content", e.target.value)} rows={10} />
        </Field>
      </div>

      <aside className="space-y-5">
        <Field label="Cover image">
          {form.cover_image_url && (
            <img src={resolveImg(form.cover_image_url)} alt="cover" className="w-full aspect-[16/9] object-cover border border-zinc-200 mb-2" />
          )}
          <label className="block">
            <input data-testid="market-form-cover-upload" type="file" accept="image/*" onChange={handleCover} className="hidden" />
            <span className="cursor-pointer inline-block w-full text-center font-mono-tech text-[10px] uppercase border border-zinc-900 px-3 py-2 hover:bg-zinc-900 hover:text-white">
              {uploadingCover ? "Uploading…" : form.cover_image_url ? "Replace cover" : "Upload cover image"}
            </span>
          </label>
          {form.cover_image_url && (
            <button type="button" onClick={() => set("cover_image_url", "")} className="mt-1 font-mono-tech text-[10px] text-red-600 hover:underline">Remove</button>
          )}
        </Field>

        <Field label="PDF report" hint="Up to 25 MB">
          {form.pdf_url && (
            <a href={resolveImg(form.pdf_url)} target="_blank" rel="noopener noreferrer" className="block font-mono-tech text-xs text-[#031833] underline mb-2 break-all">
              📄 View current PDF
            </a>
          )}
          <label className="block">
            <input data-testid="market-form-pdf-upload" type="file" accept="application/pdf" onChange={handlePdf} className="hidden" />
            <span className="cursor-pointer inline-block w-full text-center font-mono-tech text-[10px] uppercase border border-zinc-900 px-3 py-2 hover:bg-zinc-900 hover:text-white">
              {uploadingPdf ? "Uploading…" : form.pdf_url ? "Replace PDF" : "Upload PDF"}
            </span>
          </label>
          {form.pdf_url && (
            <button type="button" onClick={() => set("pdf_url", "")} className="mt-1 font-mono-tech text-[10px] text-red-600 hover:underline">Remove</button>
          )}
        </Field>

        <Field label="Report type">
          <select data-testid="market-form-type" value={form.report_type} onChange={(e) => set("report_type", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm">
            {REPORT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
        </Field>

        <Field label="Category">
          <Input data-testid="market-form-category" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Quarterly / Zone Report / Trends" />
        </Field>

        <Field label="Tags" hint="Press Enter to add">
          <div className="flex gap-2 mb-2">
            <Input data-testid="market-form-tag-input" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder="dubai, q1, jafza..." />
            <button type="button" onClick={addTag} className="font-mono-tech text-[10px] uppercase px-3 py-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white">+ Add</button>
          </div>
          {form.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {form.tags.map((t) => (
                <span key={t} className="font-mono-tech text-[10px] bg-zinc-100 px-2 py-0.5 inline-flex items-center gap-1">
                  #{t}
                  <button type="button" onClick={() => removeTag(t)} className="text-red-600">×</button>
                </span>
              ))}
            </div>
          )}
        </Field>

        <div className="space-y-2 pt-2 border-t border-zinc-200">
          <label className="flex items-center gap-2 font-mono-tech text-xs">
            <input data-testid="market-form-featured" type="checkbox" checked={form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} />
            ★ Featured (pinned at top)
          </label>
          <label className="flex items-center gap-2 font-mono-tech text-xs">
            <input data-testid="market-form-active" type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} />
            Published (visible on site)
          </label>
          <label className="flex items-center gap-2 font-mono-tech text-xs">
            <input data-testid="market-form-gated" type="checkbox" checked={form.is_gated} onChange={(e) => set("is_gated", e.target.checked)} />
            🔒 Email-gated (capture lead before full content)
          </label>
        </div>

        <button
          type="submit"
          data-testid="market-form-submit"
          disabled={saving}
          className="w-full font-mono-tech text-xs uppercase bg-[#031833] text-white px-4 py-3 hover:bg-[#0A2455] disabled:opacity-50"
        >
          {saving ? "Saving…" : submitLabel}
        </button>
      </aside>
    </form>
  );
}

function MarketList() {
  const [reports, setReports] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const fetchAll = React.useCallback(() => {
    api.get("/admin/market-reports").then((r) => {
      setReports(r.data);
      setLoading(false);
    });
  }, []);

  React.useEffect(fetchAll, [fetchAll]);

  const remove = async (id) => {
    if (!window.confirm("Delete this report? This cannot be undone.")) return;
    await api.delete(`/admin/market-reports/${id}`);
    toast.success("Deleted");
    fetchAll();
  };

  return (
    <div data-testid="admin-market-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-1">// CONTENT</div>
          <h1 className="font-display text-4xl tracking-tighter">MARKET ANALYTICS ({reports.length})</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/market/leads" data-testid="admin-market-leads-btn" className="font-mono-tech text-xs uppercase px-4 py-3 border border-zinc-900 hover:bg-zinc-900 hover:text-white">
            View leads
          </Link>
          <Link to="/admin/market/new" data-testid="admin-market-new-btn" className="bg-[#031833] text-white font-mono-tech text-xs uppercase px-4 py-3 hover:bg-[#0A2455]">
            + NEW REPORT
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="font-mono-tech text-xs text-zinc-500">Loading…</div>
      ) : reports.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-12 text-center bg-white">
          <div className="font-display text-2xl tracking-tighter mb-2">No reports yet</div>
          <p className="text-zinc-500 text-sm mb-4">Publish your first market report to start capturing leads.</p>
          <Link to="/admin/market/new" className="inline-block font-mono-tech text-xs uppercase bg-[#031833] text-white px-4 py-2">+ New report</Link>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="font-mono-tech text-[10px] uppercase text-zinc-500">
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">Category</th>
                <th className="text-center p-3">Type</th>
                <th className="text-center p-3">Status</th>
                <th className="text-left p-3">Published</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="p-3">
                    <div className="font-medium">{r.title}</div>
                    <div className="font-mono-tech text-[10px] text-zinc-400">/market-analytics/{r.slug}</div>
                  </td>
                  <td className="p-3 font-mono-tech text-xs">{r.category}</td>
                  <td className="p-3 text-center font-mono-tech text-[10px] uppercase">{r.report_type}</td>
                  <td className="p-3 text-center space-x-1">
                    {r.is_featured && <span className="bg-[#FFCC00] text-zinc-950 font-mono-tech text-[9px] px-1.5 py-0.5">★</span>}
                    {r.is_gated && <span className="bg-[#031833] text-white font-mono-tech text-[9px] px-1.5 py-0.5">🔒</span>}
                    <span className={`font-mono-tech text-[9px] uppercase px-1.5 py-0.5 ${r.is_active ? "bg-green-100 text-green-800" : "bg-zinc-100 text-zinc-500"}`}>
                      {r.is_active ? "Live" : "Draft"}
                    </span>
                  </td>
                  <td className="p-3 font-mono-tech text-xs">
                    {new Date(r.published_at).toLocaleDateString("en-AE", { year: "numeric", month: "short", day: "numeric" })}
                  </td>
                  <td className="p-3 text-right space-x-3">
                    <Link to={`/market-analytics/${r.slug}`} target="_blank" className="font-mono-tech text-xs text-zinc-600 hover:underline">View</Link>
                    <Link to={`/admin/market/${r.id}/edit`} data-testid={`market-edit-${r.slug}`} className="font-mono-tech text-xs text-[#031833] hover:underline">Edit</Link>
                    <button onClick={() => remove(r.id)} data-testid={`market-delete-${r.slug}`} className="font-mono-tech text-xs text-red-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MarketNew() {
  const nav = useNavigate();
  const submit = async (form) => {
    try {
      await api.post("/admin/market-reports", form);
      toast.success("Report created");
      nav("/admin/market");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    }
  };
  return (
    <div data-testid="admin-market-new">
      <Link to="/admin/market" className="font-mono-tech text-[10px] uppercase text-[#031833] hover:underline mb-3 inline-block">← Back to reports</Link>
      <h1 className="font-display text-4xl tracking-tighter mb-8">NEW REPORT</h1>
      <ReportForm onSubmit={submit} submitLabel="Publish report →" />
    </div>
  );
}

function MarketEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [initial, setInitial] = React.useState(null);

  const fetchOne = React.useCallback(() => {
    api.get("/admin/market-reports").then((r) => {
      const found = r.data.find((x) => x.id === id);
      setInitial(found || false);
    });
  }, [id]);

  React.useEffect(fetchOne, [fetchOne]);

  const submit = async (form) => {
    try {
      await api.put(`/admin/market-reports/${id}`, form);
      toast.success("Report updated");
      nav("/admin/market");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  if (initial === null) return <div className="font-mono-tech text-xs">Loading…</div>;
  if (initial === false) return <div className="font-mono-tech text-xs text-red-600">Report not found</div>;

  return (
    <div data-testid="admin-market-edit">
      <Link to="/admin/market" className="font-mono-tech text-[10px] uppercase text-[#031833] hover:underline mb-3 inline-block">← Back to reports</Link>
      <h1 className="font-display text-4xl tracking-tighter mb-8">EDIT REPORT</h1>
      <ReportForm initial={initial} onSubmit={submit} submitLabel="Save changes →" />
    </div>
  );
}

function MarketLeads() {
  const [leads, setLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const fetchLeads = React.useCallback(() => {
    api.get("/admin/market-leads").then((r) => { setLeads(r.data); setLoading(false); });
  }, []);

  React.useEffect(fetchLeads, [fetchLeads]);

  const exportCsv = () => {
    const header = ["Name", "Email", "Phone", "Company", "Report", "Date"];
    const rows = leads.map((l) => [l.name, l.email, l.phone || "", l.company || "", l.report_title, l.created_at]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `market-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div data-testid="admin-market-leads">
      <Link to="/admin/market" className="font-mono-tech text-[10px] uppercase text-[#031833] hover:underline mb-3 inline-block">← Back to reports</Link>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-4xl tracking-tighter">LEADS ({leads.length})</h1>
        {leads.length > 0 && (
          <button data-testid="market-leads-export" onClick={exportCsv} className="font-mono-tech text-xs uppercase px-4 py-3 border border-zinc-900 hover:bg-zinc-900 hover:text-white">
            ⬇ Export CSV
          </button>
        )}
      </div>
      {loading ? (
        <div className="font-mono-tech text-xs text-zinc-500">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-12 text-center bg-white">
          <div className="font-display text-2xl tracking-tighter mb-2">No leads yet</div>
          <p className="text-zinc-500 text-sm">Email-gated downloads will appear here.</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="font-mono-tech text-[10px] uppercase text-zinc-500">
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Phone</th>
                <th className="text-left p-3">Company</th>
                <th className="text-left p-3">Report</th>
                <th className="text-left p-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="p-3">{l.name}</td>
                  <td className="p-3 font-mono-tech text-xs"><a href={`mailto:${l.email}`} className="text-[#031833] hover:underline">{l.email}</a></td>
                  <td className="p-3 font-mono-tech text-xs">{l.phone || "—"}</td>
                  <td className="p-3 font-mono-tech text-xs">{l.company || "—"}</td>
                  <td className="p-3 font-mono-tech text-xs">{l.report_title}</td>
                  <td className="p-3 font-mono-tech text-xs">{new Date(l.created_at).toLocaleString("en-AE", { dateStyle: "medium", timeStyle: "short" })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminMarket() {
  return (
    <Routes>
      <Route index element={<MarketList />} />
      <Route path="new" element={<MarketNew />} />
      <Route path="leads" element={<MarketLeads />} />
      <Route path=":id/edit" element={<MarketEdit />} />
    </Routes>
  );
}
